import { GIButton, Modal } from 'app-components';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { INPUT_TYPES } from '../../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../../constants/inputs/validationTypes';
import { ModalContextType } from '../../../../contexts/modalContext';
import Form from '../../../../lib/wrappers/Form';
import UniversalInput from '../../../../lib/wrappers/UniversalInput';

import { TEXTAREA_MAX_LENGTH, TEXTAREA_MIN_LENGTH } from '../../../../constants/enrollments/enrollments.constants';
import { validateTextArea } from '../common/enrollmentUtils';
import { IEnrollmentResendModal } from '../common/types';
import { StyledCharacterCounter, StyledModalContent, StyledTextAreaContainer } from './ModalStyles';
import { ModalTransactionError, ModalTransactionSuccess } from './ModalSuccessError';
import { MODAL_KEYS } from './modalKeys';

interface IModalProps extends ModalContextType {
    encEnrollmentId: string;
    resend: IEnrollmentResendModal;
}

interface ICommentState {
    comment: string;
}

const ResendTransactionsModal = ({
    closeModal,
    encEnrollmentId,
    resend: {
        handleSubmit,
        content: { title, body, eventType },
        successMessage,
        errorMessage,
        clearState,
    },
}: IModalProps) => {
    const intl = useIntl();
    const { isSuccess, isError } = useSelector((state: any) => state?.memberEnrollment?.enrollmentRequest);

    const [value, setValue] = useState<ICommentState>({ comment: '' });
    const maxChars = useMemo(() => TEXTAREA_MAX_LENGTH - value.comment.length, [value.comment]);

    const handleClose = () => {
        closeModal(MODAL_KEYS.RESEND_TRANSACTIONS);
        clearState();
    };

    const handleContinue = () => {
        handleSubmit({ encEnrollmentId, eventType, comment: value.comment });
    };

    const onFieldChange = inputValue => {
        const value = validateTextArea(inputValue);
        setValue({ comment: value });
    };

    return (
        <Modal id={MODAL_KEYS.RESEND_TRANSACTIONS} data-testid="resend-transactions-modal">
            <Modal.Header
                id="resendTransactionsModalTitle"
                titleId={title}
                onHide={handleClose}
                data-testid="modal-header"
            />
            <Modal.Body data-testid="modal-body">
                <ModalTransactionSuccess message={successMessage} isTrue={isSuccess} />
                <ModalTransactionError
                    message={errorMessage}
                    isTrue={isError}
                    heading="cap.applications.modal.csrsubmissionfailtitle"
                />

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <>
                        <StyledModalContent data-testid="modal-content">{body}</StyledModalContent>
                        <Form formName="resend-transactions-form" data-testid="resend-transactions-form">
                            <StyledTextAreaContainer data-testid="textarea-container">
                                <UniversalInput
                                    label={{
                                        text: intl.formatMessage({ id: 'member.enrollment.modal.textarea.label' }),
                                        labelClasses: ['spl__width--01'],
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.TEXTAREA,
                                        name: 'comment',
                                        value: value.comment,
                                        liClass: ['text-left'],
                                        currentData: value,
                                        placeholder: intl.formatMessage({
                                            id: 'member.enrollment.modal.textarea.placeholder',
                                        }),
                                        rows: 8,
                                        validationType: VALIDATION_TYPES.LENGTH,
                                        valuesToMatch: [TEXTAREA_MIN_LENGTH, TEXTAREA_MAX_LENGTH],
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: ({ comment }) => {
                                            onFieldChange(comment);
                                        },
                                    }}
                                    data-testid="textarea-input"
                                />
                            </StyledTextAreaContainer>
                            <StyledCharacterCounter data-testid="character-counter">
                                {maxChars} {intl.formatMessage({ id: 'member.enrollment.modal.textLeft' })}
                            </StyledCharacterCounter>
                        </Form>
                    </>
                </ConditionalRendering>
            </Modal.Body>
            <Modal.Controls data-testid="modal-controls">
                <ConditionalRendering isTrue={isSuccess || isError}>
                    <GIButton
                        id="button"
                        onClickHandler={handleClose}
                        variant="secondary"
                        labelId="button.ok"
                        data-testid="ok-button"
                    />
                </ConditionalRendering>

                <ConditionalRendering isTrue={!isSuccess && !isError}>
                    <>
                        <GIButton
                            isDisabled={!value.comment.length}
                            id="button"
                            onClickHandler={handleContinue}
                            variant="primary"
                            labelId="button.continue"
                            data-testid="continue-button"
                        />
                        <GIButton
                            id="button"
                            onClickHandler={handleClose}
                            variant="secondary"
                            labelId="button.cancel"
                            data-testid="cancel-button"
                        />
                    </>
                </ConditionalRendering>
            </Modal.Controls>
        </Modal>
    );
};

export default ResendTransactionsModal;
